import { type CSSProperties, type FC, type MouseEvent, type ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { RiArrowRightWideFill, RiInformationLine, RiSquareFill } from 'react-icons/ri';
import { useModalAction } from '@/utils/modal';
import Spinner from '@/components/UI/Spinner/Spinner';
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import { type Color, type ID } from '@/model/Common.model';
import { type GroupApiId } from '@/model/TreatmentGroup.model';
import { type StudyApiId } from '@/model/Study.model';
import { type AnimalApiId } from '@/model/Animal.model';
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import { web as webRoute } from '@/support/route';
import {
  type GroupDimension,
  type MetaField,
  type SampleDefinitionMap,
  type GroupMap,
  type GroupByNumberMap,
  type SampleTypeMap,
  type AnimalMap,
  type ReservedSampleAggregation,
  type ReservedSample,
  type ChildData,
  type RootKey,
  type Level1Key,
  type Level2Key,
  type LeafKey,
  MAX_CHIPS,
  toRootKey,
  toLevel1Key,
  toLevel2Key,
} from './SampleNames.model';
import {
  getDimensionLabel,
  capitalise,
  renderTimepoint,
  renderTimepointList,
  renderSampleTypes,
  resolveDisplayValue,
  renderCollectedStatus,
  renderCount,
} from './SampleNames.utils';
import { SampleNamesTableProvider } from './SampleNamesTableProvider';
import { useSampleNamesTableContext } from './SampleNamesTableContext';
import styles from './SampleNamesTable.module.scss';

const TreeCell: FC<{
  open?: boolean;
  clickable?: boolean;
  loading?: boolean;
  depth: number;
  onClick?: () => void;
  leaf?: boolean;
  testElement: string;
  children: ReactNode;
}> = ({ open, clickable = false, loading = false, depth, onClick, leaf = false, testElement, children }) => (
  <div
    className={cn(
      leaf ? styles.LeafCell : styles.Cell,
      styles.TreeCell,
      clickable && styles.Clickable,
      open === true ? styles.Open : open === false ? styles.Closed : undefined
    )}
    style={{ '--depth': depth } as CSSProperties}
    onClick={onClick}
    onKeyDown={
      clickable
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.();
            }
          }
        : undefined
    }
    role={clickable ? 'button' : undefined}
    tabIndex={clickable ? 0 : undefined}
    aria-expanded={clickable ? open : undefined}
    data-test-component="TreeCell"
    data-test-element={testElement}
  >
    {clickable &&
      (loading ? (
        <Spinner
          size="small"
          color="blue"
          className={styles.TreeCellSpinner}
          style={{ width: '1rem', height: '1rem' }}
        />
      ) : (
        <RiArrowRightWideFill className="f5" />
      ))}
    {children}
  </div>
);

const EmptyRow: FC = () => <div className={styles.InlineCell}>No data for this grouping configuration</div>;

/**
 * Renders the expanded child section of a tree row: leaf names when terminal,
 * or a fallback subtree (NestedRows) when not. Handles error and empty states.
 */
const ChildContent: FC<{
  childEntry: ChildData<unknown> | undefined;
  groupings: Array<GroupDimension>;
  terminal: boolean;
  fallback: ReactNode;
}> = ({ childEntry, groupings, terminal, fallback }) => {
  if (!terminal) {
    return <>{fallback}</>;
  }
  if (childEntry?.status === 'error') {
    return (
      <div className={styles.InlineCell}>
        <ApiErrorBanner errorType={ApiErrorType.FETCH} error={childEntry.error} />
      </div>
    );
  }
  if (childEntry?.status === 'loaded') {
    const names = childEntry.data as Array<ReservedSample>;
    return names.length === 0 ? <EmptyRow /> : <LeafRows names={names} groupings={groupings} />;
  }
  return null;
};

const GroupColourChips: FC<{
  groupNumbers: Array<number>;
  groupByNumberMap: GroupByNumberMap;
}> = ({ groupNumbers, groupByNumberMap }) => {
  const overflow = groupNumbers.length > MAX_CHIPS;
  const visibleNumbers = overflow ? groupNumbers.slice(0, MAX_CHIPS - 1) : groupNumbers;
  const hiddenCount = overflow ? groupNumbers.length - (MAX_CHIPS - 1) : 0;

  return (
    <div className={styles.GroupChipList}>
      {visibleNumbers.reduce<Array<ReactNode>>((acc, no) => {
        const group = groupByNumberMap.get(no);
        if (_notNil(group)) {
          acc.push(
            <div
              key={no}
              className={styles.GroupChipItem}
              style={{ backgroundColor: group.color ?? undefined }}
              title={group.name ?? String(no)}
            >
              <span className={styles.GroupChipCount}>{no}</span>
            </div>
          );
        }
        return acc;
      }, [])}
      {overflow && (
        <div className={styles.GroupChipOverflowItem} title={`${hiddenCount} more groups`}>
          <span className={styles.GroupChipCount}>+{hiddenCount}</span>
        </div>
      )}
    </div>
  );
};

const StudyGroupsCell: FC<{
  groupNumbers: Array<number> | null | undefined;
  groupByNumberMap: GroupByNumberMap;
  plannedAnimals: Record<string, number>;
  animalCountByGroup: Record<string, number>;
}> = ({ groupNumbers, groupByNumberMap, plannedAnimals, animalCountByGroup }) => {
  const { openModal } = useModalAction();

  const validGroupNumbers = (groupNumbers ?? [])
    .reduce<Array<number>>((acc, n) => {
      if (_notNil(n)) {
        acc.push(n);
      }
      return acc;
    }, [])
    .sort((a, b) => a - b);
  if (_isEmpty(validGroupNumbers)) {
    return null;
  }

  const handleInfoClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openModal('SAMPLE_NAMES_GROUP_INFO', {
      groupNumbers: validGroupNumbers,
      groupByNumberMap: Object.fromEntries(groupByNumberMap),
      plannedAnimals,
      animalCountByGroup,
    });
  };

  return (
    <div className={styles.StudyGroupsCell}>
      <GroupColourChips groupNumbers={validGroupNumbers} groupByNumberMap={groupByNumberMap} />
      <button
        type="button"
        className={styles.GroupInfoButton}
        aria-label="View study group info"
        onClick={handleInfoClick}
      >
        <RiInformationLine size={14} className="mid-gray hover-blue" />
      </button>
    </div>
  );
};

const DimensionValue: FC<{
  dimension: GroupDimension;
  aggregation: ReservedSampleAggregation;
  groupMap: GroupMap;
  sampleTypeMap: SampleTypeMap;
  animalMap: AnimalMap;
}> = ({ dimension, aggregation, groupMap, sampleTypeMap, animalMap }) => {
  if (dimension === 'studyGroup') {
    const treatmentGroup = groupMap.get(aggregation.key as GroupApiId);
    const groupNo = aggregation.study_group_numbers?.[0];
    const color: Color | null = treatmentGroup?.color ?? null;
    return (
      <div className="flex flex-row items-center gap-h-1">
        {_notNil(groupNo) && <span>{`${groupNo} - `}</span>}
        {_notNil(color) && <RiSquareFill color={color} />}
        <span>{aggregation.name ?? (_isNil(aggregation.key) ? 'Unassigned' : capitalise(aggregation.key))}</span>
      </div>
    );
  }
  return <span>{resolveDisplayValue(dimension, aggregation.key, aggregation.name, sampleTypeMap, animalMap)}</span>;
};

const LeafRows: FC<{ names: Array<ReservedSample>; groupings: Array<GroupDimension> }> = ({ names, groupings }) => {
  const { groupMap, sampleDefinitionMap, sampleTypeMap, animalMap, studyId } = useSampleNamesTableContext();
  const sortedNames = useMemo(
    () =>
      [...names].sort((a, b) => {
        const defA = _notNil(a.sample_definition_api_id)
          ? sampleDefinitionMap.get(a.sample_definition_api_id)
          : undefined;
        const defB = _notNil(b.sample_definition_api_id)
          ? sampleDefinitionMap.get(b.sample_definition_api_id)
          : undefined;
        const nameA = _notNil(defA) ? (sampleTypeMap.get(defA.sample_type) ?? defA.sample_type) : '';
        const nameB = _notNil(defB) ? (sampleTypeMap.get(defB.sample_type) ?? defB.sample_type) : '';
        return nameA.localeCompare(nameB);
      }),
    [names, sampleDefinitionMap, sampleTypeMap]
  );
  return (
    <>
      {sortedNames.map((name) => {
        const plannedAnimalDisplay = name.planned_animal_id ?? '';
        const leafGroup = _notNil(name.study_group_api_id)
          ? groupMap.get(name.study_group_api_id as GroupApiId)
          : undefined;
        const leafGroupNo = leafGroup?.no ?? null;
        const sampleDef = _notNil(name.sample_definition_api_id)
          ? sampleDefinitionMap.get(name.sample_definition_api_id)
          : undefined;
        const sampleTypeName = _notNil(sampleDef)
          ? (sampleTypeMap.get(sampleDef.sample_type) ?? sampleDef.sample_type)
          : '';
        const animalEntry = _notNil(name.animal_api_id) ? animalMap.get(name.animal_api_id as AnimalApiId) : undefined;
        const animalNumericId =
          _notNil(animalEntry) && animalEntry.type === 'registered' && _notNil(animalEntry.id) ? animalEntry.id : null;

        return (
          <div key={name.api_id} className={styles.LeafRow} data-test-component="LeafRow">
            <TreeCell depth={groupings.length} leaf testElement="leaf-name">
              {name.name}
            </TreeCell>
            <div className={styles.LeafCell} data-test-element="study-groups">
              {_notNil(leafGroup) && (
                <div className="flex flex-row items-center gap-h-1">
                  {_notNil(leafGroupNo) && <span>{`${leafGroupNo} -`}</span>}
                  {_notNil(leafGroup.color) && <RiSquareFill color={leafGroup.color} />}
                  <span>{leafGroup.name}</span>
                </div>
              )}
            </div>
            <div className={styles.LeafCell} data-test-element="planned-animals">
              {plannedAnimalDisplay}
            </div>
            <div className={styles.LeafCell} data-test-element="animals">
              {_notNil(animalNumericId) ? (
                <Link
                  to={webRoute('animals.show', { id: studyId, subject_id: animalNumericId })}
                  className="link blue"
                  target="_blank"
                  rel="noreferrer"
                >
                  {name.animal_name}
                </Link>
              ) : (
                (name.animal_name ?? '')
              )}
            </div>
            <div className={styles.LeafCell} data-test-element="status">
              {capitalise(name.status)}
            </div>
            <div className={styles.LeafCell} data-test-element="timepoint">
              {_notNil(name.timepoint) ? renderTimepoint(name.timepoint) : ''}
            </div>
            <div className={styles.LeafCell} data-test-element="sample-types">
              {sampleTypeName}
            </div>
          </div>
        );
      })}
    </>
  );
};

/**
 * Renders level-1 or level-2 child rows for an expanded tree node, fetching aggregations
 * from context on first render. `depth=1` shows level-1 children of a root row;
 * `depth=2` shows level-2 children of a level-1 row.
 */
const NestedRows: FC<{ depth: 1 | 2; parentKeys: Array<string> }> = ({ depth, parentKeys }) => {
  const {
    level1Children,
    level2Children,
    leafNames,
    openLevel1,
    openLevel2,
    groupings,
    metaFields,
    groupMap,
    sampleTypeMap,
    animalMap,
    animalCountByGroup,
    groupByNumberMap,
    toggleLevel1,
    toggleLevel2,
  } = useSampleNamesTableContext();

  const rootKey = parentKeys[0] as RootKey;
  const level1KeyRaw = parentKeys[1];
  const cacheKeyPrefix = depth === 1 ? rootKey : toLevel1Key(rootKey, level1KeyRaw ?? '');
  const dimension = groupings[depth];
  const entry = depth === 1 ? level1Children.get(rootKey) : level2Children.get(cacheKeyPrefix as Level1Key);
  const openSet = depth === 1 ? openLevel1 : openLevel2;
  const isTerminal = depth === 2 || groupings.length < 3;

  if (_isNil(dimension) || _isNil(entry) || entry.status === 'loading') {
    return null;
  }
  if (entry.status === 'error') {
    return (
      <div className={styles.InlineCell}>
        <ApiErrorBanner errorType={ApiErrorType.FETCH} error={entry.error} />
      </div>
    );
  }
  if (entry.data.length === 0) {
    return <EmptyRow />;
  }

  return (
    <>
      {entry.data.map((item) => {
        const itemCacheKey: Level1Key | Level2Key =
          depth === 1 ? toLevel1Key(rootKey, item.key) : toLevel2Key(rootKey, level1KeyRaw ?? '', item.key);
        const isOpen = (openSet as Set<Level1Key | Level2Key>).has(itemCacheKey);
        const toggle =
          depth === 1
            ? () => toggleLevel1(rootKey, item.key)
            : () => toggleLevel2(rootKey, level1KeyRaw ?? '', item.key);
        const childEntry = isTerminal
          ? leafNames.get(itemCacheKey as LeafKey)
          : level2Children.get(itemCacheKey as Level1Key);
        const childLoading = isOpen && childEntry?.status === 'loading';

        return (
          <div key={item.key} className={styles.NestedRow} data-test-component="NestedRow" data-test-depth={depth}>
            <div data-test-element="container" style={{ display: 'contents' }}>
              <TreeCell
                depth={depth}
                clickable
                loading={childLoading}
                open={isOpen}
                onClick={toggle}
                testElement={`level${depth}`}
              >
                <div className={styles.TreeCellContent}>
                  <span className={styles.DimensionLabel}>{getDimensionLabel(dimension, metaFields)}</span>
                  <div className={styles.TreeCellRow}>
                    <DimensionValue
                      dimension={dimension}
                      aggregation={item}
                      groupMap={groupMap}
                      sampleTypeMap={sampleTypeMap}
                      animalMap={animalMap}
                    />
                    <span className={styles.CountBadge}>{item.total_count}</span>
                  </div>
                </div>
              </TreeCell>
              <div className={styles.Cell} data-test-element="study-groups">
                <StudyGroupsCell
                  groupNumbers={item.study_group_numbers}
                  groupByNumberMap={groupByNumberMap}
                  plannedAnimals={item.planned_animals}
                  animalCountByGroup={animalCountByGroup}
                />
              </div>
              <div className={styles.Cell} data-test-element="planned-animals">
                {renderCount(item.planned_animal_count)}
              </div>
              <div className={styles.Cell} data-test-element="animals">
                {renderCount(item.animal_count)}
              </div>
              <div className={styles.Cell} data-test-element="status">
                {renderCollectedStatus(item.collected_count, item.total_count)}
              </div>
              <div className={styles.Cell} data-test-element="timepoints">
                {renderTimepointList(item.timepoints)}
              </div>
              <div className={styles.Cell} data-test-element="sample-types">
                {renderSampleTypes(item.sample_types, sampleTypeMap)}
              </div>
            </div>
            {isOpen && !childLoading && (
              <ChildContent
                childEntry={childEntry}
                groupings={groupings}
                terminal={isTerminal}
                fallback={<NestedRows depth={2} parentKeys={[...parentKeys, item.key]} />}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

const RootRow: FC<{ item: ReservedSampleAggregation }> = ({ item }) => {
  const {
    openRoots,
    level1Children,
    leafNames,
    groupings,
    metaFields,
    toggleRoot,
    groupMap,
    sampleTypeMap,
    animalMap,
    animalCountByGroup,
    groupByNumberMap,
  } = useSampleNamesTableContext();
  const rootKey = toRootKey(item.key);
  const isOpen = openRoots.has(rootKey);
  const isSingleGrouping = groupings.length === 1;
  const childEntry = isSingleGrouping ? leafNames.get(rootKey) : level1Children.get(rootKey);
  const childLoading = isOpen && childEntry?.status === 'loading';

  return (
    <div
      className={styles.RootRow}
      data-test-component="RootRow"
      data-test-element="container"
      data-test-key={item.key}
    >
      <div style={{ display: 'contents' }}>
        <TreeCell
          depth={0}
          clickable
          loading={childLoading}
          open={isOpen}
          onClick={() => toggleRoot(rootKey)}
          testElement="root"
        >
          <div className={styles.TreeCellContent}>
            <span className={styles.DimensionLabel}>{getDimensionLabel(groupings[0], metaFields)}</span>
            <div className={styles.TreeCellRow}>
              <DimensionValue
                dimension={groupings[0]}
                aggregation={item}
                groupMap={groupMap}
                sampleTypeMap={sampleTypeMap}
                animalMap={animalMap}
              />
              <span className={styles.CountBadge}>{item.total_count}</span>
            </div>
          </div>
        </TreeCell>
        <div className={styles.Cell} data-test-element="study-groups">
          <StudyGroupsCell
            groupNumbers={item.study_group_numbers}
            groupByNumberMap={groupByNumberMap}
            plannedAnimals={item.planned_animals}
            animalCountByGroup={animalCountByGroup}
          />
        </div>
        <div className={styles.Cell} data-test-element="planned-animals">
          {renderCount(item.planned_animal_count)}
        </div>
        <div className={styles.Cell} data-test-element="animals">
          {renderCount(item.animal_count)}
        </div>
        <div className={styles.Cell} data-test-element="status">
          {renderCollectedStatus(item.collected_count, item.total_count)}
        </div>
        <div className={styles.Cell} data-test-element="timepoints">
          {renderTimepointList(item.timepoints)}
        </div>
        <div className={styles.Cell} data-test-element="sample-types">
          {renderSampleTypes(item.sample_types, sampleTypeMap)}
        </div>
      </div>
      {isOpen && !childLoading && (
        <ChildContent
          childEntry={childEntry}
          groupings={groupings}
          terminal={isSingleGrouping}
          fallback={<NestedRows depth={1} parentKeys={[item.key]} />}
        />
      )}
    </div>
  );
};

const TableHeader: FC = () => (
  <div className={styles.Header}>
    <div className={styles.HeaderCell}>Sample Names</div>
    <div className={styles.HeaderCell}>Study Groups</div>
    <div className={styles.HeaderCell}>Planned Animals</div>
    <div className={styles.HeaderCell}>Animals</div>
    <div className={styles.HeaderCell}>Status</div>
    <div className={styles.HeaderCell}>Timepoints</div>
    <div className={styles.HeaderCell}>Sample Types</div>
  </div>
);

/**
 * Expandable tree table for viewing reserved sample names grouped by up to three dimensions.
 * Lazily fetches child aggregations and leaf names as rows are expanded.
 */
export const SampleNamesTable: FC<{
  studyApiId: StudyApiId;
  studyId: ID;
  definitions: Array<ReservedSampleAggregation>;
  groupMap: GroupMap;
  groupings: Array<GroupDimension>;
  metaFields: Array<MetaField>;
  sampleTypeMap: SampleTypeMap;
  animalMap: AnimalMap;
  sampleDefinitionMap: SampleDefinitionMap;
}> = ({
  studyApiId,
  studyId,
  definitions,
  groupMap,
  groupings,
  metaFields,
  sampleTypeMap,
  animalMap,
  sampleDefinitionMap,
}) => (
  <SampleNamesTableProvider
    studyApiId={studyApiId}
    studyId={studyId}
    groupMap={groupMap}
    groupings={groupings}
    metaFields={metaFields}
    sampleTypeMap={sampleTypeMap}
    animalMap={animalMap}
    sampleDefinitionMap={sampleDefinitionMap}
  >
    <div className={styles.SampleNamesTable} data-test-component="SampleNamesTable" data-test-element="container">
      <TableHeader />
      {definitions.map((definition) => (
        <RootRow key={definition.key} item={definition} />
      ))}
    </div>
  </SampleNamesTableProvider>
);
